import { useState, useCallback, useEffect, useRef } from "react";
import { useSpeechSynthesis } from "./useSpeechSynthesis";

const VOICE_MODE_STORAGE_KEY = "wixar_voice_mode_enabled";

interface UseConversationModeOptions {
  /** Fired ~300ms after TTS finishes naturally while voice mode is on.
   *  The consumer wires this to dictation.startListening() so the mic
   *  re-arms for the user's reply. */
  onAutoListenCue?: () => void;
  /** Fired when voice mode is turned off. The consumer should ensure
   *  the dictation hook stops listening (this hook doesn't own the mic). */
  onDeactivate?: () => void;
}

interface UseConversationModeReturn {
  voiceModeEnabled: boolean;
  toggleVoiceMode: () => void;
  setVoiceModeEnabled: (enabled: boolean) => void;
  beginSpeakingSession: () => Promise<void>;
  pushSpeakingChunk: (chunk: string) => void;
  endSpeakingSession: () => void;
  stopSpeaking: () => void;
  speak: (text: string) => Promise<void>;
  isSpeaking: boolean;
  /** Manually arm/disarm the auto-listen cue.
   *  Use disarm when the user explicitly cancels (e.g. clicked mic mid-listen)
   *  to prevent a stale cue from re-arming after the next isSpeaking flip. */
  armAutoListen: () => void;
  disarmAutoListen: () => void;
  error: Error | null;
}

export function useConversationMode(
  options: UseConversationModeOptions = {},
): UseConversationModeReturn {
  const [voiceModeEnabled, setVoiceModeEnabledState] = useState(() => {
    return localStorage.getItem(VOICE_MODE_STORAGE_KEY) === "true";
  });

  const {
    speak: ttsSpeak,
    beginSession: ttsBeginSession,
    pushText: ttsPushText,
    endSession: ttsEndSession,
    stop: ttsStop,
    prewarm: ttsPrewarm,
    isSpeaking,
    error: ttsError,
  } = useSpeechSynthesis();

  const shouldAutoListenRef = useRef(false);
  const wasSpeakingRef = useRef(false);

  const cbRef = useRef(options);
  cbRef.current = options;

  useEffect(() => {
    localStorage.setItem(VOICE_MODE_STORAGE_KEY, voiceModeEnabled.toString());
    if (voiceModeEnabled) ttsPrewarm();
  }, [voiceModeEnabled, ttsPrewarm]);

  useEffect(() => {
    const justFinishedSpeaking =
      wasSpeakingRef.current && !isSpeaking && shouldAutoListenRef.current;
    wasSpeakingRef.current = isSpeaking;
    if (!justFinishedSpeaking || !voiceModeEnabled) return undefined;
    shouldAutoListenRef.current = false;
    const timer = setTimeout(() => {
      cbRef.current.onAutoListenCue?.();
    }, 300);
    return () => clearTimeout(timer);
  }, [isSpeaking, voiceModeEnabled]);

  const setVoiceModeEnabled = useCallback(
    (enabled: boolean) => {
      setVoiceModeEnabledState(enabled);
      if (!enabled) {
        ttsStop();
        shouldAutoListenRef.current = false;
        cbRef.current.onDeactivate?.();
      }
    },
    [ttsStop],
  );

  const toggleVoiceMode = useCallback(() => {
    setVoiceModeEnabled(!voiceModeEnabled);
  }, [voiceModeEnabled, setVoiceModeEnabled]);

  const beginSpeakingSession = useCallback(async (): Promise<void> => {
    if (!voiceModeEnabled) return;
    shouldAutoListenRef.current = true;
    return ttsBeginSession();
  }, [voiceModeEnabled, ttsBeginSession]);

  const pushSpeakingChunk = useCallback(
    (chunk: string): void => {
      if (!voiceModeEnabled) return;
      ttsPushText(chunk);
    },
    [voiceModeEnabled, ttsPushText],
  );

  const endSpeakingSession = useCallback((): void => {
    if (!voiceModeEnabled) return;
    ttsEndSession();
  }, [voiceModeEnabled, ttsEndSession]);

  const speak = useCallback(
    async (text: string): Promise<void> => {
      if (!voiceModeEnabled) return;
      shouldAutoListenRef.current = true;
      return ttsSpeak(text);
    },
    [voiceModeEnabled, ttsSpeak],
  );

  const stopSpeaking = useCallback(() => {
    shouldAutoListenRef.current = false;
    ttsStop();
  }, [ttsStop]);

  const armAutoListen = useCallback(() => {
    shouldAutoListenRef.current = true;
  }, []);

  const disarmAutoListen = useCallback(() => {
    shouldAutoListenRef.current = false;
  }, []);

  return {
    voiceModeEnabled,
    toggleVoiceMode,
    setVoiceModeEnabled,
    beginSpeakingSession,
    pushSpeakingChunk,
    endSpeakingSession,
    stopSpeaking,
    speak,
    isSpeaking,
    armAutoListen,
    disarmAutoListen,
    error: ttsError,
  };
}
